import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import { listPromptsQueryKey, useUpdatePrompt } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
	queryClient,
	useAIEmbeddedInfiniteList,
	useAuthUser,
	useDeleteAIEmbedded,
	useUpdateAIEmbedded,
} from '../../api';
import { aiEmbeddedQueryKeyFactory } from '../../api/hooks/ai/constants';
import { useUserPreferences } from '../../api/hooks/user/useUserPreferences';
import { AIAssistantMode } from '../../components/AIAssistant/types';
import { useAIAssistantContext } from '../../components/AIAssistant/useAIAssistantContext';
import { getTimePeriod } from '../../components/AIAssistant/utils';
import { openConfirmModal } from '../../components/ModalManager';
import { useDragReorder } from '../../hooks/useDragReorder';
import { useFeatureFlags } from '../../utils/featureFlags';
import { AiChatHistory } from './components/AiChatHistory';
import { AiChatHistorySkeleton } from './components/AiChatHistorySkeleton';

function groupChats(data: EmbeddedPromptParentOut[], pinnedChatIds: string[]) {
	return (data ?? []).reduce(
		(
			acc: {
				[key: string]: EmbeddedPromptParentOut[];
			},
			chat
		) => {
			// If the chat is pinned, add it to the pinned group
			if (pinnedChatIds.includes(chat.id)) {
				return {
					...acc,
					Pinned: [...(acc['Pinned'] || []), chat],
				};
			}

			// Otherwise, add it to time period groups
			const period = getTimePeriod(chat.created_at);
			return {
				...acc,
				[period]: [...(acc[period] || []), chat],
			};
		},
		{} as {
			[key: string]: EmbeddedPromptParentOut[];
		}
	);
}

type ChatHistoryFilter = 'all' | 'human' | 'agent';

interface EmbeddedPromptWithAgent extends EmbeddedPromptParentOut {
	is_agent_run?: boolean | null;
}

const InternalAiChatHistoryController = observer(
	({ filter = 'all' }: { filter?: ChatHistoryFilter }) => {
		const [editingChatId, setEditingChatId] = useState<string | null>(null);
		const [editingChatTitle, setEditingChatTitle] = useState('');

		const { secodaAiTasks } = useFeatureFlags();

		const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
			useAIEmbeddedInfiniteList({
				filters:
					secodaAiTasks && filter !== 'all'
						? { is_agent_run: filter === 'agent' ? true : false }
						: {},
			});

		const results = useMemo(
			() => (data?.pages ?? []) as EmbeddedPromptWithAgent[],
			[data?.pages]
		);

		const {
			pinnedAiChatIds,
			updatePinnedChatsOptimistic,
		}: {
			pinnedAiChatIds: string[];
			updatePinnedChatsOptimistic: (
				pinnedChatIds: string[]
			) => Promise<string[]>;
		} = useUserPreferences();

		const { orderedPinnedChats, regularChats } = useMemo(() => {
			if (!data) {
				return { orderedPinnedChats: [], regularChats: {} };
			}

			const groupedChats = groupChats(results, pinnedAiChatIds);
			const { Pinned: pinned, ...rest } = groupedChats;

			const orderedPinned = pinnedAiChatIds
				.map((chatId: string) => pinned?.find((chat) => chat.id === chatId))
				.filter(
					(chat: EmbeddedPromptParentOut | undefined) => chat !== undefined
				);

			return { orderedPinnedChats: orderedPinned, regularChats: rest };
		}, [results, data, pinnedAiChatIds]);

		const { reorder: handlePinnedChatDragEnd, items: displayedPinnedChats } =
			useDragReorder(orderedPinnedChats, updatePinnedChatsOptimistic);

		const location = useLocation();

		const { user, workspace } = useAuthUser();
		const {
			mode,
			setId,
			setIsHistoryOpen,
			id: activeChatId,
		} = useAIAssistantContext();

		const navigate = useNavigate();

		const { mutate: deleteChat } = useDeleteAIEmbedded({
			invalidationKeys: [
				aiEmbeddedQueryKeyFactory.allLists(),
				listPromptsQueryKey({}),
			],
		});

		const { mutate: updateChat } = useUpdateAIEmbedded({
			invalidationKeys: [
				aiEmbeddedQueryKeyFactory.allLists(),
				listPromptsQueryKey({}),
			],
		});

		const { mutate: markAgentRunsRead } = useUpdatePrompt({
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: aiEmbeddedQueryKeyFactory.allLists(),
				});
				queryClient.invalidateQueries({
					queryKey: listPromptsQueryKey({}),
				});
			},
		});

		const handleTogglePin = useCallback(
			(chatId: string) => {
				const pinnedChatIds =
					displayedPinnedChats?.map(
						(chat: EmbeddedPromptParentOut) => chat.id
					) ?? [];

				if (pinnedChatIds.includes(chatId)) {
					updatePinnedChatsOptimistic(
						pinnedChatIds.filter((id: string) => id !== chatId)
					);
				} else {
					updatePinnedChatsOptimistic([...pinnedChatIds, chatId]);
				}
			},
			[displayedPinnedChats, updatePinnedChatsOptimistic]
		);

		// Sensors for drag-and-drop
		const sensors = useSensors(
			useSensor(PointerSensor, {
				activationConstraint: {
					distance: 8,
				},
			})
		);

		const handleSelectChat = useCallback(
			(chat: EmbeddedPromptParentOut) => {
				markAgentRunsRead({
					pathParams: { promptId: chat.id },
					body: { mark_agent_runs_read: true },
				});
				trackEvent(
					'ai/history/select',
					{
						prompt_id: chat.id,
						prompt: chat.prompt ?? '',
						mode,
						url: location.pathname,
					},
					user,
					workspace
				);

				if (mode === AIAssistantMode.SIDEBAR) {
					setId(chat.id);
					setIsHistoryOpen(false);
				} else {
					navigate(`/ai/${chat.id}`);
				}
			},
			[
				mode,
				navigate,
				setId,
				setIsHistoryOpen,
				location.pathname,
				user,
				workspace,
				markAgentRunsRead,
			]
		);

		const handleDeleteChat = useCallback(
			(chat: EmbeddedPromptParentOut) => {
				openConfirmModal({
					title: 'Delete chat?',
					centered: true,
					children: <Text size="sm">You can&apos;t undo this action.</Text>,
					labels: { confirm: 'Delete', cancel: 'Cancel' },
					confirmProps: { variant: 'primary', tone: 'critical' },
					onConfirm: () => {
						trackEvent(
							'ai/history/delete',
							{
								prompt_id: chat.id,
								prompt: chat.prompt ?? '',
								mode,
								url: location.pathname,
							},
							user,
							workspace
						);

						// If chat being deleted is current page, navigate to new chat
						const currentPath = location.pathname;
						const isActiveChat = currentPath === `/ai/${chat.id}`;

						deleteChat({ id: chat.id });

						if (isActiveChat) {
							navigate('/ai/new');
						}
					},
				});
			},
			[deleteChat, location.pathname, mode, user, workspace, navigate]
		);

		const handleStartEditingChatTitle = useCallback(
			(chatId: string, currentTitle: string) => {
				setEditingChatId(chatId);
				setEditingChatTitle(currentTitle);
			},
			[]
		);

		const handleStopEditingChatTitle = useCallback(() => {
			setEditingChatId(null);
			setEditingChatTitle('');
		}, []);

		const handleSaveEditingChatTitle = useCallback(
			(chatId: string) => {
				const trimmedTitle = editingChatTitle.trim();
				if (trimmedTitle) {
					updateChat(
						{
							data: { id: chatId, title: trimmedTitle },
						},
						{
							onSettled: () => {
								// Only stop editing mode after mutation ends to avoid flickering between the old and new title
								handleStopEditingChatTitle();
							},
						}
					);
				} else {
					handleStopEditingChatTitle();
				}
			},
			[editingChatTitle, updateChat, handleStopEditingChatTitle]
		);

		if (isLoading) {
			return <AiChatHistorySkeleton />;
		}

		return (
			<AiChatHistory
				groupedChats={regularChats}
				pinnedChats={displayedPinnedChats}
				onSelectChat={handleSelectChat}
				onDeleteChat={handleDeleteChat}
				editingChatId={editingChatId}
				editingChatTitle={editingChatTitle}
				onStartEditingChatTitle={handleStartEditingChatTitle}
				onStopEditingChatTitle={handleStopEditingChatTitle}
				onSaveEditingChatTitle={handleSaveEditingChatTitle}
				onModifyEditingChatTitle={setEditingChatTitle}
				activeChatId={activeChatId}
				onTogglePin={handleTogglePin}
				onPinnedChatDragEnd={handlePinnedChatDragEnd}
				sensors={sensors}
				onLoadMore={fetchNextPage}
				hasMore={!!hasNextPage}
				isLoadingMore={isFetchingNextPage}
			/>
		);
	}
);

export const AiChatHistoryController = memo(
	({ filter = 'all' }: { filter?: ChatHistoryFilter }) => (
		<Suspense fallback={<AiChatHistorySkeleton />}>
			<InternalAiChatHistoryController filter={filter} />
		</Suspense>
	)
);
